import { Modal, Typography } from 'antd';
import { Geocoder } from '@mapbox/search-js-react';
import { useState, useEffect } from 'react';
import { useCreateTenantLocationMutation, useUpdateTenantLocationMutation } from '../../../domain/tenantLocations/tenantLocationsQueries';
import { TenantLocation, TenantLocationType } from '../../../domain/tenantLocations/tenantLocation';

const { Text } = Typography;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  proximity?: string | undefined
  shopAddress: TenantLocation | undefined
  onAddressUpdate?: (coordinates: { latitude: number; longitude: number }) => void
  closeable?: boolean
}

export const ManageShopAddressModal = ({isOpen, onClose, proximity = undefined, shopAddress, onAddressUpdate, closeable = true}: Props) => {
  const [selectedAddress, setSelectedAddress] = useState<any>(null);
  const [showGeocoder, setShowGeocoder] = useState(false);
  const {mutate: createTenantLocation} = useCreateTenantLocationMutation();
  const {mutate: updateTenantLocation} = useUpdateTenantLocationMutation();

  useEffect(() => {
    if (!isOpen) {
      setSelectedAddress(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        setShowGeocoder(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setShowGeocoder(false);
    }
  }, [isOpen]);

  const handleAddressRetrieve = (res: any) => {
    setSelectedAddress(res);
  };

  const handleOk = () => {
    if (selectedAddress) {
      const coordinates = {
        latitude: selectedAddress.geometry.coordinates[1],
        longitude: selectedAddress.geometry.coordinates[0]
      };

      const request = {
        type: TenantLocationType.Shop,
        address1: selectedAddress.properties.context.address.name,
        address2: null,
        city: selectedAddress.properties.context.place.name,
        stateCode: selectedAddress.properties.context.region.region_code,
        zip: selectedAddress.properties.context.postcode.name,
        county: selectedAddress.properties.context.district.name,
        countryCode: selectedAddress.properties.context.country.country_code,
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        active: true
      }

      const onSuccess = () => {
        onAddressUpdate?.(coordinates);
        onClose();
      };

      if (shopAddress) {
        updateTenantLocation(
          { id: shopAddress.id, request },
          { onSuccess }
        );
      } else {
        createTenantLocation(
          request,
          { onSuccess }
        );
      }
    } else {
      onClose();
    }
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      onOk={handleOk}
      okText="Save"
      cancelText="Cancel"
      okButtonProps={{
        disabled: !selectedAddress
      }}
      cancelButtonProps={{
        disabled: !closeable && !shopAddress
      }}
      title="Manage Shop Address"
      closable={closeable}
      maskClosable={closeable}
    >
      <div>
        <Text type="secondary" style={{ fontSize: '10px' }}>Shop Address</Text>
      </div>
      <div style={{ marginBottom: '16px', marginTop: '4px' }}>
        {showGeocoder && (
          
          <Geocoder
            key="geocoder-shop-address"
            accessToken={(window as any).env.MAPBOX_ACCESS_TOKEN}
            placeholder="Search for an address"
            onRetrieve={handleAddressRetrieve}
            theme={{
              variables: {
                borderRadius: '6px',
                colorPrimary: '#1677ff',
                colorBackground: '#ffffff',
                colorText: 'rgba(0, 0, 0, 0.88)',
                colorSecondary: 'rgba(0, 0, 0, 0.45)',
              }
            }}
            options={{
              proximity: proximity
            }}
          />
        )}
      </div>
      
      {selectedAddress && (
        <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
          <Text strong>Selected Shop Address:</Text>
          <div style={{ marginTop: '8px' }}>
            <Text style={{ color: 'rgba(0, 0, 0, 0.75)', fontSize: '14px' }}>{selectedAddress.place_name}</Text>
          </div>
          <div style={{ marginTop: '4px' }}>
            <Text style={{ color: 'rgba(0, 0, 0, 0.65)', fontSize: '14px' }}>
              {selectedAddress.properties.full_address}
            </Text>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ManageShopAddressModal